import classNames from 'classnames'
import { useEffect, useMemo, useRef, useState } from 'react'
import { Size } from '@opallabs/gem'
import { noop } from '@opallabs/lambda'
import { Icon, SearchInput } from '@opallabs/lapidary'
import {
  type CalendarFilterCategory,
  type CalendarFilterMode,
  type CalendarFilterOption,
  type CalendarFilterOptionGroup,
} from '../types'
import {
  buildValueTree,
  type CalendarValueTree,
  chosenUnderParent,
  parentFilterMode,
} from '../valueTree'
import { FilterCount } from './FilterCount'
import { ModeSelect } from './ModeSelect'
import { OptionMarks, OptionRow, type TriState, TriStateBox } from './OptionRow'
import * as styles from './styles'

/** The list the category filters by, and the values carved out of it. */
export type ModeLists = Readonly<{
  carved: readonly string[]
  chosen: readonly string[]
}>

export const listsFor = (
  mode: CalendarFilterMode,
  include: readonly string[],
  exclude: readonly string[]
): ModeLists =>
  mode === 'include'
    ? { carved: exclude, chosen: include }
    : { carved: include, chosen: exclude }

/** Whether the category's list holds this value, on its own or through its row. */
export const isChosen = (
  valueId: string,
  { carved, chosen }: ModeLists,
  tree: CalendarValueTree
): boolean => {
  if (chosen.includes(valueId)) return true

  const parentId = tree.parentOf.get(valueId)
  return (
    parentId !== undefined &&
    chosen.includes(parentId) &&
    !carved.includes(valueId)
  )
}

/** How much of what sits under a row the category's list holds. */
export const parentState = (
  parentId: string,
  { carved, chosen }: ModeLists,
  tree: CalendarValueTree
): TriState => {
  const children = tree.childrenOf.get(parentId) ?? []

  if (chosen.includes(parentId))
    return children.some((child) => carved.includes(child)) ? 'mixed' : 'true'
  if (children.length > 0 && children.every((child) => chosen.includes(child)))
    return 'true'

  return children.some((child) => chosen.includes(child)) ? 'mixed' : 'false'
}

/** Select all over some rows: checked when all of them are, part when some are. */
const allOf = (states: readonly TriState[]): TriState =>
  states.length > 0 && states.every((state) => state === 'true')
    ? 'true'
    : states.some((state) => state !== 'false')
      ? 'mixed'
      : 'false'

type OptionSection = Readonly<{
  group?: CalendarFilterOptionGroup
  options: readonly CalendarFilterOption[]
}>

/**
 * The options in the order they were given, split at each change of heading.
 *
 * Options that belong to no group come first, so a list nobody grouped reads
 * exactly as it did before. Everything else keeps the order its group first
 * appeared in.
 */
export const optionSections = (
  options: readonly CalendarFilterOption[]
): OptionSection[] => {
  const ungrouped = options.filter((option) => option.group === undefined)
  const sections = new Map<string, CalendarFilterOption[]>()
  const groups = new Map<string, CalendarFilterOptionGroup>()

  options.forEach((option) => {
    const { group } = option
    if (group === undefined) return

    groups.set(group.id, group)
    const found = sections.get(group.id)
    if (found === undefined) sections.set(group.id, [option])
    else found.push(option)
  })

  return [
    ...(ungrouped.length === 0 ? [] : [{ options: ungrouped }]),
    ...[...sections].map(([groupId, grouped]) => ({
      group: groups.get(groupId),
      options: grouped,
    })),
  ]
}

type LeafSearchProps = Readonly<{
  /** What the search box searches, for the landmark it makes. */
  context: string
  htmlId: string
  label: string
  onSearch: (query: string) => void
}>

/** The search over a list of rows, squared to sit level with them. */
export const LeafSearch: React.FC<LeafSearchProps> = ({
  context,
  htmlId,
  label,
  onSearch,
}) => (
  <SearchInput
    context={context}
    id={htmlId}
    labelText={label}
    onSearch={onSearch}
    shape="square"
  />
)
LeafSearch.displayName = 'Calendar: LeafSearch'

/** The row above a list that stands for the whole of it. */
const SelectAllRow: React.FC<{
  onToggle: () => void
  state: TriState
}> = ({ onToggle, state }) => (
  <li>
    <button
      aria-checked={state}
      className={styles.selectors.selectAll}
      onClick={onToggle}
      role="checkbox"
      type="button"
    >
      <TriStateBox state={state} />
      <span className={styles.selectors.text}>Select all</span>
    </button>
  </li>
)
SelectAllRow.displayName = 'Calendar: SelectAllRow'

type ParentRowProps = Readonly<{
  /** How many of the values under this row are in the filter. */
  count: number
  icon?: React.ReactNode
  /**
   * The control that says which way round this row is read, where the row
   * answers for itself. Left out where the category answers for every row.
   */
  modeSelect?: React.ReactNode
  onOpen: () => void
  onToggle: () => void
  option: CalendarFilterOption
  /** Focus comes back here from the tier this row opens. */
  openRef: (row: HTMLButtonElement | null) => void
  state: TriState
}>

/**
 * A row that stands for the values under it: check it to choose the whole of
 * it, or open it to pick from what it holds.
 *
 * The chevron is the last thing on the row, after the mode control where the
 * row carries one. It stays inside the button that opens the row all the same:
 * a second control named for the same thing would be one way in too many, so
 * the stylesheet lifts it to the trailing edge instead.
 */
const ParentRow: React.FC<ParentRowProps> = ({
  count,
  icon,
  modeSelect,
  onOpen,
  onToggle,
  option,
  openRef,
  state,
}) => (
  <div className={styles.selectors.parentRow}>
    <button
      aria-checked={state}
      aria-label={option.label}
      className={styles.selectors.parentCheck}
      onClick={onToggle}
      role="checkbox"
      type="button"
    >
      <TriStateBox state={state} />
    </button>
    <button
      ref={openRef}
      // The count beside the name is drawn for the eye alone, so the row says
      // it here instead of leaving a number nobody can hear.
      aria-label={
        count === 0
          ? `Open ${option.label}`
          : `Open ${option.label}, ${count} chosen`
      }
      className={styles.selectors.parentOpen}
      onClick={onOpen}
      type="button"
    >
      <OptionMarks icon={icon} option={option} />
      <span aria-hidden={true} className={styles.selectors.parentCount}>
        <FilterCount count={count} />
      </span>
      <span aria-hidden={true} className={styles.selectors.parentChevron}>
        <Icon
          ariaHidden={true}
          className={styles.selectors.lineArrow}
          glyph="gem-chevron-e"
          size={Size.Icon}
        />
      </span>
    </button>
    {modeSelect}
  </div>
)
ParentRow.displayName = 'Calendar: ParentRow'

export type OptionLeafProps = Readonly<{
  category: CalendarFilterCategory
  /** The values this category hides, as the filter state holds them. */
  exclude: readonly string[]
  /** The values this category matches, as the filter state holds them. */
  include: readonly string[]
  /** The category's own mode, where the category makes the choice once. */
  mode: CalendarFilterMode
  onModeChange: (mode: CalendarFilterMode) => void
  onOpenParent: (parentId: string) => void
  /** What a row with nothing ticked in it was told to be, where it has been. */
  parentModes?: Readonly<Record<string, CalendarFilterMode>>
  /** Sets the mode of one row, where each row answers for itself. */
  onSetParentMode: (parentId: string, mode: CalendarFilterMode) => void
  /** Select all over the rows on show, which a search narrows. */
  onToggleAll: (optionIds: readonly string[]) => void
  onToggleValue: (valueId: string) => void
  /** The parent whose values are open in the tier beside this one, if any. */
  openParentId: string | null
  renderOptionIcon?: (
    category: CalendarFilterCategory,
    option: CalendarFilterOption
  ) => React.ReactNode
}>

/**
 * The second pane: the top of one category.
 *
 * A row that stands for others — a channel over its accounts, a label set over
 * its labels — is checkable and openable at once: check it to choose all of it,
 * open it to pick from what it holds. Everything else is a plain row.
 *
 * Where the mode belongs to the category, this is the only tier that sets it:
 * people filter at the channel level, and hiding a whole channel must not mean
 * opening it first. Setting the mode here sets it for the tier below as well.
 *
 * Where each row answers for itself — a label set matched beside a label set
 * hidden — there is nothing over the whole category to set, so each row carries
 * a dropdown of its own instead.
 *
 * Every tier has a search over its own rows. On a tier of rows to open it finds
 * the row; on a flat category it finds the value and draws each match under the
 * row it belongs to.
 */
export const OptionLeaf: React.FC<OptionLeafProps> = ({
  category,
  exclude,
  include,
  mode,
  onModeChange,
  onOpenParent,
  onSetParentMode,
  onToggleAll,
  onToggleValue,
  openParentId,
  parentModes,
  renderOptionIcon,
}) => {
  const [search, setSearch] = useState('')

  const tree = useMemo(
    () => buildValueTree(category.options, category.modeScope),
    [category.modeScope, category.options]
  )

  const openRows = useRef(new Map<string, HTMLButtonElement | null>())
  const openRow = useRef<string | null>(null)

  // The tier under a parent replaces these rows rather than opening beside
  // them, so the row the keyboard was on stops existing. Hand focus back to it
  // on the way out, or the drawer loses its place.
  useEffect(() => {
    if (openParentId === null && openRow.current !== null)
      openRows.current.get(openRow.current)?.focus()
    openRow.current = openParentId
  }, [openParentId])

  // Which list one value is read against. A category that makes the choice
  // once reads every value against the same list; Labels asks the set the
  // value belongs to, because a set matched sits beside a set hidden.
  const isByParent = tree.modeScope === 'parent'
  const filter = { exclude, include, parentModes }
  const modeOf = (valueId: string) =>
    isByParent ? parentFilterMode(valueId, filter, tree) : mode
  const listsOf = (valueId: string) =>
    listsFor(modeOf(valueId), include, exclude)

  // A row belongs at the top of this tier unless something above it is offered
  // as a row of its own: an account sits under its channel, a label under its
  // set. A group nothing offers as an option heads nothing a person can check,
  // so what it holds is offered here rather than hidden behind it.
  const offered = new Set(category.options.map((option) => option.id))
  const topLevel = category.options.filter((option) => {
    const parentId = tree.parentOf.get(option.id)
    return parentId === undefined || !offered.has(parentId)
  })

  // A tier with rows to open is drawn as the design's category rows, rule to
  // rule; a flat category is drawn as a list.
  const hasParentRows = topLevel.some((option) =>
    tree.childrenOf.has(option.id)
  )

  const query = search.trim().toLowerCase()
  const matches = (option: CalendarFilterOption) =>
    option.label.toLowerCase().includes(query)
  const shown = (hasParentRows ? topLevel : category.options).filter(matches)
  const sections = optionSections(shown)

  // Select all speaks for the rows on show, in the list the category filters
  // by, so it reads the carve-outs back out of what that list holds.
  const selectAllState = allOf(
    shown.map((option) =>
      tree.childrenOf.has(option.id)
        ? parentState(option.id, listsOf(option.id), tree)
        : isChosen(option.id, listsOf(option.id), tree)
          ? 'true'
          : 'false'
    )
  )

  return (
    <div className={styles.selectors.leaf}>
      {isByParent ? null : (
        <div className={styles.selectors.leafControls}>
          <ModeSelect
            htmlName={`calendar-filter-mode-${category.id}`}
            mode={mode}
            onChange={onModeChange}
          />
        </div>
      )}

      <div
        className={classNames(styles.selectors.listBlock, {
          [styles.selectors.parentTier]: hasParentRows,
        })}
      >
        <LeafSearch
          context={category.label}
          htmlId={`calendar-filter-search-${category.id}`}
          label={`Search ${category.label.toLowerCase()}`}
          onSearch={setSearch}
        />

        <ul className={styles.selectors.optionList}>
          {/*
          A set that answers for itself cannot be chosen alongside a set that
          answers the other way, so a Select all over the sets has no one
          meaning. The design draws none.
        */}
          {category.shouldOfferSelectAll === false ||
          isByParent ||
          shown.length === 0 ? null : (
            <SelectAllRow
              onToggle={() => onToggleAll(shown.map((option) => option.id))}
              state={selectAllState}
            />
          )}

          {!hasParentRows
            ? sections.map(({ group, options }) => {
                // A channel is an option and the heading its accounts sit
                // under, so the heading is drawn as the row it stands for
                // rather than twice. A group nobody offers as a row is not a
                // filter at all, and heads nothing a person could see.
                const heading = options.find(
                  (option) => option.id === group?.id
                )
                const nested = options.filter((option) => option !== heading)

                return (
                  <li key={group?.id ?? ''}>
                    {heading === undefined ? null : (
                      <OptionRow
                        categoryId={category.id}
                        checked={isChosen(
                          heading.id,
                          listsOf(heading.id),
                          tree
                        )}
                        icon={renderOptionIcon?.(category, heading)}
                        onToggle={() => onToggleValue(heading.id)}
                        option={heading}
                        partChosenState={
                          tree.childrenOf.has(heading.id)
                            ? parentState(heading.id, listsOf(heading.id), tree)
                            : undefined
                        }
                      />
                    )}
                    <ul
                      className={
                        group === undefined
                          ? undefined
                          : styles.selectors.optionGroup
                      }
                    >
                      {nested.map((option) => (
                        <li key={option.id}>
                          <OptionRow
                            categoryId={category.id}
                            checked={isChosen(
                              option.id,
                              listsOf(option.id),
                              tree
                            )}
                            icon={renderOptionIcon?.(category, option)}
                            onToggle={() => onToggleValue(option.id)}
                            option={option}
                          />
                        </li>
                      ))}
                    </ul>
                  </li>
                )
              })
            : shown.map((option) => (
                <li key={option.id}>
                  {!tree.childrenOf.has(option.id) ? (
                    <OptionRow
                      categoryId={category.id}
                      checked={isChosen(option.id, listsOf(option.id), tree)}
                      icon={renderOptionIcon?.(category, option)}
                      onToggle={() => onToggleValue(option.id)}
                      option={option}
                    />
                  ) : (
                    <ParentRow
                      count={chosenUnderParent(
                        option.id,
                        include,
                        exclude,
                        tree
                      )}
                      icon={renderOptionIcon?.(category, option)}
                      modeSelect={
                        !isByParent ? undefined : (
                          <ModeSelect
                            context={option.label}
                            htmlName={`calendar-filter-mode-${category.id}-${option.id}`}
                            isCompact={true}
                            mode={modeOf(option.id)}
                            onChange={(next) =>
                              onSetParentMode(option.id, next)
                            }
                          />
                        )
                      }
                      onOpen={() => onOpenParent(option.id)}
                      onToggle={() => onToggleValue(option.id)}
                      option={option}
                      openRef={(row) => {
                        openRows.current.set(option.id, row)
                      }}
                      state={parentState(option.id, listsOf(option.id), tree)}
                    />
                  )}
                </li>
              ))}

          {shown.length === 0 ? (
            <li className={styles.selectors.leafEmpty}>No matches</li>
          ) : null}
        </ul>
      </div>
    </div>
  )
}
OptionLeaf.displayName = 'Calendar: OptionLeaf'

export type ChildOptionLeafProps = Readonly<{
  category: CalendarFilterCategory
  exclude: readonly string[]
  include: readonly string[]
  /** The mode these rows tick in: the category's, or the parent's own. */
  mode: CalendarFilterMode
  /**
   * Changes the parent's own mode, where the parent answers for itself. Left
   * out where the mode belongs to the whole category, which this tier cannot
   * see enough of to set.
   */
  onModeChange?: (mode: CalendarFilterMode) => void
  /** Select all over the values on show, which a search narrows. */
  onToggleAll: (optionIds: readonly string[]) => void
  onToggleValue: (valueId: string) => void
  /** The row this tier sits under: its accounts, or its labels. */
  parent: CalendarFilterOption
  renderOptionIcon?: (
    category: CalendarFilterCategory,
    option: CalendarFilterOption
  ) => React.ReactNode
}>

/**
 * The third pane: the values under one parent.
 *
 * Where the mode belongs to the whole category it is set a pane back, not here:
 * setting it beside these rows would turn the parent's own tick around as well,
 * and the parent is not on this pane to be seen turning. The drawer's header
 * says which mode these rows are ticked in instead.
 *
 * Where the parent answers for itself, the control belongs here as much as it
 * does on the parent's row: both say the one thing about the one set.
 *
 * Select all over every value here is the parent itself, so choosing it is
 * choosing the channel rather than each of its accounts. A value under a chosen
 * parent is already in the filter, and unchecking it leaves just that one out —
 * which is why a row can read checked without being written down.
 */
export const ChildOptionLeaf: React.FC<ChildOptionLeafProps> = ({
  category,
  exclude,
  include,
  mode,
  onModeChange,
  onToggleAll,
  onToggleValue,
  parent,
  renderOptionIcon,
}) => {
  const [search, setSearch] = useState('')

  const tree = useMemo(
    () => buildValueTree(category.options, category.modeScope),
    [category.modeScope, category.options]
  )

  const query = search.trim().toLowerCase()
  const childIds = tree.childrenOf.get(parent.id) ?? []
  const shown = category.options.filter(
    (option) =>
      childIds.includes(option.id) && option.label.toLowerCase().includes(query)
  )

  const lists = listsFor(mode, include, exclude)

  return (
    <div className={styles.selectors.leaf}>
      <div className={styles.selectors.leafControls}>
        {/*
          Where the mode belongs to the whole category, this tier ticks in it
          without being able to set it, so the control is shown but not offered.
        */}
        <ModeSelect
          htmlName={`calendar-filter-mode-${category.id}-${parent.id}-pane`}
          isDisabled={onModeChange === undefined}
          mode={mode}
          onChange={onModeChange ?? noop}
        />
      </div>

      <div className={styles.selectors.listBlock}>
        <LeafSearch
          context={parent.label}
          htmlId={`calendar-filter-search-${category.id}-${parent.id}`}
          label={`Search ${parent.label}`}
          onSearch={setSearch}
        />

        <ul className={styles.selectors.optionList}>
          {shown.length === 0 ? null : (
            <SelectAllRow
              onToggle={() => onToggleAll(shown.map((option) => option.id))}
              state={allOf(
                shown.map((option) =>
                  isChosen(option.id, lists, tree) ? 'true' : 'false'
                )
              )}
            />
          )}

          {shown.map((option) => (
            <li key={option.id}>
              <OptionRow
                categoryId={category.id}
                checked={isChosen(option.id, lists, tree)}
                icon={renderOptionIcon?.(category, option)}
                onToggle={() => onToggleValue(option.id)}
                option={option}
              />
            </li>
          ))}

          {shown.length === 0 ? (
            <li className={styles.selectors.leafEmpty}>No matches</li>
          ) : null}
        </ul>
      </div>
    </div>
  )
}
ChildOptionLeaf.displayName = 'Calendar: ChildOptionLeaf'
